/**
 * Public route component that redirects authenticated users
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getReturnPathFromState, useAuth } from '../../shared/auth';

interface PublicRouteProps {
  children: React.ReactNode;
}

// Routes that should always be accessible, even when authenticated
// These handle cross-domain SSO flows where tokens need to be processed
const BYPASS_AUTH_REDIRECT_PATHS = [
  '/auth/sso/callback',
  '/auth/sso/native-callback',
  '/auth/sso/desktop-callback', // Legacy — remove after desktop-callback URI removed from Google Console
  '/auth/token-exchange',
  '/oauth/callback/google',
  '/oauth/callback/microsoft',
  // Ad-lead connectors (Marketing → Lead Capture): the user is authenticated when
  // they return from Facebook Login, and the callback must process code/state to
  // finalize the connection — redirecting to home would drop the OAuth params.
  '/oauth/callback/meta',
  '/oauth/callback/whatsapp',
  // Shopify channel connect (Inventory → Integrations): the merchant is signed in
  // when they return from the Shopify grant screen; the callback must process
  // code/state/hmac to store the offline token — redirecting home would drop them.
  '/oauth/callback/shopify',
  // Stripe Connect (Accounting → Online Payments): the admin is signed in when
  // they return from the Stripe grant screen; the callback must process code/state
  // to store the connected account — redirecting home would drop the OAuth params.
  '/oauth/callback/stripe',
  // Notion import connect (Tasks → Import from Notion): the admin is signed in when
  // they return from the Notion grant screen; the callback must process code/state
  // (bouncing cross-domain if needed) — redirecting home would drop the OAuth params.
  '/oauth/callback/notion',
  // Plaid Link OAuth cross-domain resume (Accounting → Bank Feeds): the canonical-
  // host page runs the whole Link session for a custom / sysadmin-domain admin.
  // A canonical-host admin who lands here IS authenticated — redirecting home
  // would abort the in-flight Link resume.
  '/oauth/plaid/resume',
  // WhatsApp Coexistence Embedded Signup (Marketing → Lead Capture): FB.login is
  // refused on any origin outside the Meta app's JS-SDK allowed-domain list, so the
  // canonical-host page runs the whole popup for a custom / sysadmin-domain admin.
  // A canonical-host admin who lands here IS authenticated — redirecting home would
  // abort the connect before the popup ever opened.
  '/oauth/whatsapp-embedded-signup',
];

export function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Check if current path should bypass authentication redirect
  const shouldBypassAuthRedirect = BYPASS_AUTH_REDIRECT_PATHS.some(
    path => location.pathname.startsWith(path)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Allow SSO callback routes to process even when authenticated
  // This is needed for cross-domain SSO where the main domain may have
  // an existing session but needs to process the callback for a custom domain
  if (isAuthenticated && !shouldBypassAuthRedirect) {
    // Redirect to the intended destination or home. This branch wins the race
    // against LoginPage's own post-login navigate as soon as `isAuthenticated`
    // flips, so it must restore the FULL path — query string and hash included
    // (`/settings?tab=notifications` losing its tab was the reported bug).
    return <Navigate to={getReturnPathFromState(location.state)} replace />;
  }

  return <>{children}</>;
}
